import { type CSSProperties, type ReactNode, useEffect, useRef } from 'react';

export interface CheckboxProps {
  checked?: boolean;
  /** Dash state for a partially selected table. */
  indeterminate?: boolean;
  onChange?: (checked: boolean) => void;
  /** Visible label. For a bare box (table cells) pass `ariaLabel` instead. */
  label?: ReactNode;
  ariaLabel?: string;
  disabled?: boolean;
  style?: CSSProperties;
}

/** Square checkbox: table selection, filters. */
export function Checkbox({ checked = false, indeterminate = false, onChange, label, ariaLabel, disabled = false, style }: CheckboxProps) {
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (input.current) input.current.indeterminate = indeterminate;
  }, [indeterminate]);
  const on = checked || indeterminate;
  const classes = ['fs-checkbox'];
  if (disabled) classes.push('fs-checkbox--disabled');
  return (
    <label className={classes.join(' ')} style={style}>
      <input
        ref={input}
        type="checkbox"
        className="fs-visually-hidden"
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(event) => onChange?.(event.target.checked)}
      />
      <span className={on ? 'fs-checkbox__box fs-checkbox__box--on' : 'fs-checkbox__box'} aria-hidden="true">
        {indeterminate ? (
          <span className="fs-checkbox__dash" />
        ) : (
          checked && (
            <svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M2 6.5 L5 9.5 L10 2.5" />
            </svg>
          )
        )}
      </span>
      {label ? <span>{label}</span> : null}
    </label>
  );
}
